import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Copy, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  director: "Director",
  dispatch_executive: "Dispatch Executive",
  slitting_operator: "Slitting Operator",
  press_operator: "Press Operator",
  pending: "Pending (assign role later)",
};

const InviteUserDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("pending");
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState(false);
  const qc = useQueryClient();

  const appUrl = useMemo(() => window.location.origin, []);

  const inviteText = useMemo(() => {
    if (!createdUser) return "";
    const fname = (createdUser.name || createdUser.email.split("@")[0]).split(" ")[0];
    return `Hi ${fname},

You've been added to the SR ELECTROSTEEL PRIVATE LIMITED ERP system as ${ROLE_LABELS[createdUser.role]}.

To sign in:
1. Open ${appUrl}
2. Click "Continue with Google"
3. Use your company Gmail (${createdUser.email})

You'll land in the dashboard immediately — no waiting for approval.

If you have any trouble accessing, ping me.`;
  }, [createdUser, appUrl]);

  const reset = () => {
    setEmail(""); setName(""); setRole("pending");
    setCreatedUser(null); setCopied(false);
  };

  const inviteMut = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(`${API}/users`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
      }, { withCredentials: true });
      return data;
    },
    onSuccess: (data) => {
      setCreatedUser(data);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${data.email} added — copy the invite text below`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to add user"),
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopied(true);
      toast.success("Invite copied — paste it into Gmail");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Copy failed", err);
      toast.error("Copy failed — select text manually");
    }
  };

  const canSubmit = email.trim() && email.includes("@") && !inviteMut.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="invite-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="h-5 w-5" /> {createdUser ? "Invite ready" : "Invite a new user"}
          </DialogTitle>
        </DialogHeader>

        {!createdUser ? (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Company Gmail *</Label>
              <Input
                data-testid="invite-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. shreyas@srelectrosteel.com"
                autoFocus
                className="font-mono-data"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Name (optional)</Label>
              <Input
                data-testid="invite-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Shreyas"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Role on first sign-in</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="director">Director</SelectItem>
                  <SelectItem value="dispatch_executive">Dispatch Executive</SelectItem>
                  <SelectItem value="slitting_operator">Slitting Operator</SelectItem>
                  <SelectItem value="press_operator">Press Operator</SelectItem>
                  <SelectItem value="pending">Pending (you&apos;ll assign a role after they sign in)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">
                Pre-assigning a role means they get full access the instant they sign in. Pending = they wait on the approval screen until you assign a role.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm">
              <p className="font-semibold text-green-900 mb-0.5">User created</p>
              <p className="text-green-800">
                <span className="font-mono-data">{createdUser.email}</span> is now <strong>{ROLE_LABELS[createdUser.role]}</strong>. Copy the invite below and paste it into Gmail.
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Invite message (editable)</Label>
              <Textarea
                data-testid="invite-text"
                value={inviteText}
                onChange={() => {}}
                rows={9}
                className="font-mono text-xs"
                readOnly
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {!createdUser ? (
            <>
              <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="rounded-sm">Cancel</Button>
              <Button
                onClick={() => inviteMut.mutate()}
                disabled={!canSubmit}
                data-testid="invite-create-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
              >
                {inviteMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</> : <><UserPlus className="h-4 w-4 mr-2" /> Add user</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { reset(); }} data-testid="invite-another-btn" className="rounded-sm">Invite another</Button>
              <Button
                onClick={handleCopy}
                data-testid="invite-copy-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
              >
                {copied ? <><Check className="h-4 w-4 mr-2" /> Copied</> : <><Copy className="h-4 w-4 mr-2" /> Copy invite</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
